"use client";

import { Autocomplete as HeroAutocomplete } from "@heroui/react";
import type React from "react";
import { cn } from "../../lib/utils";
import autocompleteStyles from "./Autocomplete.module.css";

// Get the original component props types
export type AutocompleteProps<T extends object> = React.ComponentProps<typeof HeroAutocomplete<T>> & {
  className?: string;
};

// Create the wrapper component
const AutocompleteWrapper = <T extends object>({
  className,
  classNames,
  inputProps,
  children,
  ...props
}: AutocompleteProps<T>) => {
  return (
    <HeroAutocomplete
      radius="sm"
      {...props}
      classNames={{
        base: cn(className),
        ...classNames,
      }}
      inputProps={{
        ...inputProps,
        classNames: {
          ...inputProps?.classNames,
          // 16px on mobile (text-base) keeps iOS from auto-zooming on input
          // focus; shrink to text-sm at sm+. Mirrors the shared Input/DatePicker.
          input: cn("text-base sm:text-sm", inputProps?.classNames?.input),
          inputWrapper: cn(
            props.variant === "bordered" && autocompleteStyles.borderedInputWrapper,
            inputProps?.classNames?.inputWrapper,
          ),
        },
      }}
    >
      {children}
    </HeroAutocomplete>
  );
};

// Copy all static properties from the original component
Object.setPrototypeOf(AutocompleteWrapper, HeroAutocomplete);

// Copy static properties that HeroUI's collection system might need
Object.assign(AutocompleteWrapper, HeroAutocomplete);

// Ensure the component has the right displayName and type
AutocompleteWrapper.displayName = (HeroAutocomplete as React.ComponentType<unknown>).displayName || "Autocomplete";

// Copy the $$typeof symbol if it exists (React uses this for component identification)
if ("$$typeof" in HeroAutocomplete) {
  (AutocompleteWrapper as unknown as Record<string, unknown>).$$typeof = (
    HeroAutocomplete as unknown as Record<string, unknown>
  ).$$typeof;
}

// Export the wrapped component
export const Autocomplete = AutocompleteWrapper;
